import { Mutation, Action } from 'vuex-module-decorators';
import { Resolve } from 'vue-di';
import { InjectModule, InjectVuexModule } from 'vue-di/vuex';
import { EdgePatientEnrollmentApi } from '@trialcard/apigateway.client/edgePatientEnrollmentApi';
import {
    EnrollPatientRequestModel,
    EnterpriseCoordinatorAccountUpsearchRequestModel,
    UpsearchModelEnterpriseCoordinatorPatientServiceModelsPatientUpsearchModel,
    UpsearchModelEnterpriseCoordinatorAccountServiceModelsAccountUpsearchModel,
    EnterpriseCoordinatorPatientServiceModelsPatientUpsearchModel,
    EnterpriseCoordinatorSharedAddressUpsearchModel,
    EnterpriseCoordinatorSharedEmailAddressUpsearchModel,
    EnterpriseCoordinatorSharedPhoneUpsearchModel,
    UpsearchModelEnterpriseCoordinatorPayorServiceModelsPayorUpsearchModel,
    EnterpriseCoordinatorAccountServiceModelsAccountContactPreferenceModel,
    UpsearchModelEnterpriseCoordinatorPrescriberServiceModelsPrescriberUpsearchModel,
    UpsearchModelEnterpriseCoordinatorSiteServiceModelsSiteUpsearchModel,
    EnterpriseCoordinatorSiteServiceModelsSiteUpsearchModel,
    EnterpriseCoordinatorPrescriberServiceModelsPrescriberUpsearchModel,
} from '@trialcard/apigateway.models';
import { Gender, RoleType, EnrollPatientCaseInitiator } from '@trialcard/enums';

import moment, { Moment } from 'moment';
import { AxiosResponse } from 'axios';
import { EdgeEnrollmentApi } from '@trialcard/apigateway.client';
import { Settings } from '~/services/settings';

import SurveyModule from '~/store/survey';

import { rolling365 } from '~/tests/e2e/@data/shared/dates'
import { pharmacyMN, medicalMN } from '~/tests/e2e/@data/shared/memberNumbers'

// Generated EnrollPatientRequestModel omits accountAttestations from attributeTypeMap, so Serializer strips it.
// Register the field so Serializer.serialize(..., "EnrollPatientRequestModel") keeps it (Array<any> passes items through).
const enrollPatientAccountAttestationsMap = {
    name: 'accountAttestations',
    baseName: 'accountAttestations',
    type: 'Array<any>',
} as const;
if (!EnrollPatientRequestModel.attributeTypeMap.some((m) => m.name === enrollPatientAccountAttestationsMap.name)) {
    EnrollPatientRequestModel.attributeTypeMap.push({ ...enrollPatientAccountAttestationsMap });
}

@InjectModule({ stateFactory: true }, module)
export default class EnrollmentModule extends InjectVuexModule {
    @Resolve
    public patientEnrollmentApi!: EdgePatientEnrollmentApi;

    @Resolve
    public enrollmentApi!: EdgeEnrollmentApi;

    @Resolve
    public settings!: Settings;

    @Resolve
    public survey!: SurveyModule;

    isCareGiver: boolean | null = null;

    public currentPhoneType = '';
    public currentPhonePurpose = '';

    defaultSiteData =  {
        accountRelationshipIsPrimary: true,
        siteRoleType: RoleType.PrescriberSite,
        pointOfContacts: [],
        description: '',
        addresses: [
            {
                addressOne: '',
                addressTwo: '',
                zip: '',
                city: '',
                state: '',
            },
        ],
        contacts: [],
        emailAddresses: [],
        externalIds: [],
        phoneNumbers: [],
        globalIds: [],
    } as EnterpriseCoordinatorSiteServiceModelsSiteUpsearchModel

    defaultPatientInformationData = {
        firstName: '' as string,
        lastName: '' as string,
        dateOfBirth: null as Moment | null,
        addresses: [] as EnterpriseCoordinatorSharedAddressUpsearchModel[],
        emailAddresses: [] as EnterpriseCoordinatorSharedEmailAddressUpsearchModel[],
        phoneNumbers: [] as EnterpriseCoordinatorSharedPhoneUpsearchModel[],
        gender: null as Gender | null,
    };

    defaultCareGiverInformationData = {
        firstName: '' as string,
        lastName: '' as string,
        addresses: [] as EnterpriseCoordinatorSharedAddressUpsearchModel[],
        emailAddresses: [] as EnterpriseCoordinatorSharedEmailAddressUpsearchModel[],
        phoneNumbers: [] as EnterpriseCoordinatorSharedPhoneUpsearchModel[],
    };

    defaultPrescriberData = [
        {
            model: {
                firstName: '',
                lastName: '',
                npi: '',
                accountRelationshipIsPrimary: true,
                prescriberLicenses: [],
                externalIds: [],
                addresses: [],
                phoneNumbers: [],
                emailAddresses: [],
            },
        },
    ] as UpsearchModelEnterpriseCoordinatorPrescriberServiceModelsPrescriberUpsearchModel[];



    defaultPayorData = [
        {
            model: {
                payorId: (null as unknown) as number,
                payorType: (null as unknown) as number,
                accountRelationshipIsPrimary: true,
                payorGroup: '',
                payorName: '',
                webAddresses: [],
                addresses: [],
                phoneNumbers: [
                    {
                        phoneType: 1,
                        phoneNumber: '',
                        phonePurpose: 1,
                    },
                    {
                        phoneType: 3,
                        phoneNumber: '',
                        phonePurpose: 8,
                    },
                ],
                emailAddresses: [],
                contacts: [],
                payorBenefit: {
                    dateOfBirth: null as Moment | null,
                    policyHolderFirstName: '',
                    policyHolderLastName: '',
                    relationshipToPatient: undefined,
                    policyNumber: '',
                    planType: undefined,
                },
            },
        },
    ] as UpsearchModelEnterpriseCoordinatorPayorServiceModelsPayorUpsearchModel[];

    defaultContactPreferences = [] as EnterpriseCoordinatorAccountServiceModelsAccountContactPreferenceModel[];

    defaultMemberNumber = pharmacyMN.number
    defaultMedicalNumber = medicalMN.number
    defaultExpirationDate = rolling365
    defaultProgramConsent = {
        agreeEisaiTermsOfInformationUse: false,
        agreeAge18OrOlder: false,
        agreeLeqembiCopayProgramTerms: false,
    };

    medicalNumber = this.defaultMedicalNumber
    memberNumber = this.defaultMemberNumber;
    expirationDate = this.defaultExpirationDate;
    patientInformationData = { ...this.defaultPatientInformationData };
    careGiverInformationData = { ...this.defaultCareGiverInformationData };
    prescriberData = [...this.defaultPrescriberData];
    siteData = { ...this.defaultSiteData };
    payorData = [...this.defaultPayorData];
    contactPreferences = [...this.defaultContactPreferences];
    medicalMemberNumber: any;
    ncpdp = '';
    programConsent = { ...this.defaultProgramConsent };

    @Mutation
    setCurrentPhoneType(value: string){
        this.currentPhoneType = value;
    }
    @Mutation
    setCurrentPhonePurpose(value: string){
        this.currentPhonePurpose = value;
    }

    @Mutation
    public setNcpdp(ncpdp: string) {
        this.ncpdp = ncpdp;
    }

    @Mutation
    public async updatePatientInformation(newPatientInformation: typeof EnrollmentModule.prototype.patientInformationData) {
        this.patientInformationData = { ...this.patientInformationData, ...newPatientInformation };
    }

    @Mutation
    public async updatePrescribers(newPrescriberData: UpsearchModelEnterpriseCoordinatorPrescriberServiceModelsPrescriberUpsearchModel[]) {
        this.prescriberData = [...newPrescriberData];
    }

    @Mutation
    public async updatePayors(newPayorData: UpsearchModelEnterpriseCoordinatorPayorServiceModelsPayorUpsearchModel[]) {
        this.payorData = [...newPayorData];
    }

    @Mutation
    public async updateCareGiverInformation(newCareGiverInformation: typeof EnrollmentModule.prototype.careGiverInformationData) {
        this.patientInformationData = { ...this.patientInformationData, ...newCareGiverInformation };
    }

    @Mutation
    public async updateContactPreferences(newContactPreferences: EnterpriseCoordinatorAccountServiceModelsAccountContactPreferenceModel[]) {
        this.contactPreferences = [...newContactPreferences];
    }

    @Mutation
    public setMemberNumber(memberNumber: string) {
        this.memberNumber = memberNumber;
    }

    @Mutation
    public setMedicalNumber(medicalNumber: string) {
        this.medicalNumber = medicalNumber;
    }

    @Mutation
    public setExpirationDate(expirationDate: Moment) {
        this.expirationDate = expirationDate;
    }
    // @Mutation
    // public async updatePrescriberInformation(newPrescriberData: EnterpriseCoordinatorPrescriberServiceModelsPrescriberUpsearchModel) {
    //     this.prescriberData = { ...this.prescriberData, ...newPrescriberData };
    // }

    @Mutation
    public setIsCaregiver(isCareGiver: boolean | null) {
        this.isCareGiver = isCareGiver;
    }
    @Mutation
    public async updateSiteInformation(newSites: EnterpriseCoordinatorSiteServiceModelsSiteUpsearchModel) {
        this.siteData = {...this.siteData, ...newSites};
    }

    @Mutation
    public updateProgramConsent(newConsent: typeof EnrollmentModule.prototype.programConsent) {
        this.programConsent = { ...this.programConsent, ...newConsent };
    }

    @Action({ rawError: true })
    public clearStore() {
        this.setMemberNumber(this.defaultMemberNumber);
        this.setMedicalNumber(this.defaultMedicalNumber);
        this.setExpirationDate(this.defaultExpirationDate);
        this.updatePatientInformation(this.defaultPatientInformationData);
        this.updateContactPreferences(this.defaultContactPreferences);
        this.setCurrentPhoneType('');
        this.setCurrentPhonePurpose('');
        this.updateSiteInformation(this.defaultSiteData);
        this.updatePrescribers(this.defaultPrescriberData);
        this.setNcpdp('');
        this.updateProgramConsent(this.defaultProgramConsent);
    }

    @Action({ rawError: true })
    public clearEnrollment() {
        this.updatePatientInformation({ ...this.defaultPatientInformationData });
        this.setMemberNumber(this.defaultMemberNumber);
        this.setMedicalNumber(this.defaultMedicalNumber);
        this.setNcpdp('');
        this.updateProgramConsent(this.defaultProgramConsent);
    }

    @Action({ rawError: true })
    public async submitToAPI() {
        let response = null;
        const { prescriberData, siteData, payorData, contactPreferences } = this;
        try {
            const patient = {
                account: {
                    patientData: {
                        canUpdate: true,
                        canInsert: true,
                        skipSearch: false,
                        model: {
                            ...this.patientInformationData,
                            middleName: '',
                            suffix: '',
                            nickname: '',
                        } as EnterpriseCoordinatorPatientServiceModelsPatientUpsearchModel,
                    } as UpsearchModelEnterpriseCoordinatorPatientServiceModelsPatientUpsearchModel,
                    accountData: {
                        canUpdate: true,
                        canInsert: true,
                        skipSearch: false,
                        model: {
                            accountType: 1,
                            accountStatus: 1,
                            contactPreferences,
                        },
                    } as UpsearchModelEnterpriseCoordinatorAccountServiceModelsAccountUpsearchModel,
                    // siteData: [
                    //     {
                    //         canUpdate: true,
                    //         canInsert: true,
                    //         skipSearch: false,
                    //         model: {
                    //             ...this.siteData,
                    //         } as EnterpriseCoordinatorSiteServiceModelsSiteUpsearchModel,
                    //     },
                    // ] as UpsearchModelEnterpriseCoordinatorSiteServiceModelsSiteUpsearchModel[],
                    // prescriberData,
                    surveySessionData: [this.survey.sessionId] as number[],
                } as EnterpriseCoordinatorAccountUpsearchRequestModel,
                caseInitiator: EnrollPatientCaseInitiator.pharmacy,
                accountAttestations: [
                    {
                        code: 'eServiceAttestion',
                        startDate: moment()
                    },
                ],
            };
            response = await this.patientEnrollmentApi.patientEnrollmentEnrollPatient(patient, this.settings.programId);
        } catch {
            response = { status: 400 } as AxiosResponse;
        }
        return response;
    }
}
